// src/modules/Shop/pages/Cart.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaTrash, FaMinus, FaPlus, FaArrowLeft, FaShoppingBag, FaTruck } from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;

  h1 {
    font-size: 28px;
    font-weight: 700;
    color: #111;
    margin-bottom: 8px;
  }

  p {
    color: #666;
    font-size: 14px;
  }
`;

const CartLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CartItems = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #eee;
  overflow: hidden;
`;

const CartHeader = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr auto;
  gap: 16px;
  padding: 16px 24px;
  background: #f8f8f8;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;

  @media (max-width: 600px) {
    display: none;
  }
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr auto;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid #eee;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  gap: 16px;
`;

const ProductImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
`;

const ProductDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled(Link)`
  font-size: 15px;
  font-weight: 600;
  color: #111;
  text-decoration: none;
  display: block;
  margin-bottom: 4px;

  &:hover {
    color: #00D4AA;
  }
`;

const ProductSku = styled.span`
  font-size: 12px;
  color: #999;
`;

const PriceCell = styled.div`
  font-size: 15px;
  color: #333;

  @media (max-width: 600px) {
    display: flex;
    justify-content: space-between;

    &::before {
      content: 'Preco:';
      color: #666;
      font-weight: 500;
    }
  }
`;

const QuantityCell = styled.div`
  @media (max-width: 600px) {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &::before {
      content: 'Quantidade:';
      color: #666;
      font-weight: 500;
    }
  }
`;

const QuantityControls = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  background: #f5f5f5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 200ms;

  &:hover {
    background: #e5e5e5;
  }

  svg {
    font-size: 10px;
  }
`;

const QuantityValue = styled.span`
  width: 40px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
`;

const TotalCell = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111;

  @media (max-width: 600px) {
    display: flex;
    justify-content: space-between;

    &::before {
      content: 'Total:';
      color: #666;
      font-weight: 500;
    }
  }
`;

const RemoveButton = styled.button`
  width: 36px;
  height: 36px;
  background: none;
  border: 1px solid #eee;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  transition: all 200ms;

  &:hover {
    border-color: #ef4444;
    color: #ef4444;
    background: rgba(239, 68, 68, 0.05);
  }
`;

const CartSummary = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #eee;
  padding: 24px;
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
`;

const ShippingCalculator = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #eee;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 12px;

    svg {
      color: #00D4AA;
    }
  }
`;

const ShippingInput = styled.div`
  display: flex;
  gap: 8px;

  input {
    flex: 1;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #00D4AA;
    }
  }

  button {
    padding: 12px 20px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 200ms;

    &:hover {
      background: #e5e5e5;
    }
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;

  span:first-child {
    color: #666;
  }

  span:last-child {
    color: #333;
    font-weight: ${props => props.$total ? '700' : '400'};
    font-size: ${props => props.$total ? '20px' : '14px'};
  }
`;

const FreeShippingBanner = styled.div`
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  padding: 12px;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
  margin-bottom: 16px;
`;

const CheckoutButton = styled(Link)`
  display: block;
  width: 100%;
  background: #00D4AA;
  color: #000;
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: all 200ms;
  margin-bottom: 12px;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

const ContinueShoppingLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  padding: 12px;

  &:hover {
    color: #111;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 64px 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid #eee;

  svg {
    font-size: 64px;
    color: #ddd;
    margin-bottom: 24px;
  }

  h2 {
    font-size: 24px;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    color: #666;
    margin-bottom: 24px;
  }
`;

const EmptyCartButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #111;
  color: white;
  padding: 14px 28px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    opacity: 0.9;
  }
`;

const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

const Cart = () => {
  const { items, subtotal, updateQuantity, removeItem } = useCart();
  const [cep, setCep] = useState('');
  const [shipping, setShipping] = useState(null);

  const freeShippingThreshold = 500;
  const remainingForFreeShipping = freeShippingThreshold - subtotal;
  const hasFreeShipping = subtotal >= freeShippingThreshold;

  const handleCalculateShipping = () => {
    // Simulacao - integrar com Melhor Envio
    if (cep.length >= 8) {
      setShipping({
        options: [
          { name: 'PAC', price: 25.90, days: '8-12 dias uteis' },
          { name: 'SEDEX', price: 45.90, days: '3-5 dias uteis' }
        ]
      });
    }
  };

  const total = subtotal + (hasFreeShipping ? 0 : (shipping?.options?.[0]?.price || 0));

  if (items.length === 0) {
    return (
      <PageContainer>
        <EmptyCart>
          <FaShoppingBag />
          <h2>Seu carrinho esta vazio</h2>
          <p>Adicione produtos para continuar comprando</p>
          <EmptyCartButton to="/">
            <FaArrowLeft /> Ver produtos
          </EmptyCartButton>
        </EmptyCart>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <h1>Carrinho de Compras</h1>
        <p>{items.length} {items.length === 1 ? 'item' : 'itens'} no carrinho</p>
      </PageHeader>

      <CartLayout>
        <CartItems>
          <CartHeader>
            <span>Produto</span>
            <span>Preco</span>
            <span>Quantidade</span>
            <span>Total</span>
            <span></span>
          </CartHeader>

          {items.map(item => (
            <CartItem key={item.product_id}>
              <ProductInfo>
                <ProductImage
                  src={item.image || 'https://placehold.co/80x80/f5f5f5/999?text=Produto'}
                  alt={item.name}
                />
                <ProductDetails>
                  <ProductName to={`/produto/${item.slug}`}>{item.name}</ProductName>
                  {item.sku && <ProductSku>SKU: {item.sku}</ProductSku>}
                </ProductDetails>
              </ProductInfo>

              <PriceCell>{formatPrice(item.price)}</PriceCell>

              <QuantityCell>
                <QuantityControls>
                  <QuantityButton onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                    <FaMinus />
                  </QuantityButton>
                  <QuantityValue>{item.quantity}</QuantityValue>
                  <QuantityButton onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                    <FaPlus />
                  </QuantityButton>
                </QuantityControls>
              </QuantityCell>

              <TotalCell>{formatPrice(item.price * item.quantity)}</TotalCell>

              <RemoveButton onClick={() => removeItem(item.product_id)} title="Remover">
                <FaTrash />
              </RemoveButton>
            </CartItem>
          ))}
        </CartItems>

        <CartSummary>
          <SummaryTitle>Resumo do Pedido</SummaryTitle>

          <ShippingCalculator>
            <label>
              <FaTruck /> Calcular frete
            </label>
            <ShippingInput>
              <input
                type="text"
                placeholder="Digite seu CEP"
                value={cep}
                onChange={(e) => setCep(e.target.value.replace(/\D/g, ''))}
                maxLength={8}
              />
              <button onClick={handleCalculateShipping}>Calcular</button>
            </ShippingInput>

            {shipping && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
                {shipping.options.map((opt, i) => (
                  <div key={i} style={{ marginBottom: '4px' }}>
                    {opt.name}: {formatPrice(opt.price)} ({opt.days})
                  </div>
                ))}
              </div>
            )}
          </ShippingCalculator>

          {remainingForFreeShipping > 0 ? (
            <FreeShippingBanner>
              Faltam {formatPrice(remainingForFreeShipping)} para frete gratis!
            </FreeShippingBanner>
          ) : (
            <FreeShippingBanner>
              Voce ganhou frete gratis!
            </FreeShippingBanner>
          )}

          <SummaryRow>
            <span>Subtotal</span>
            <span>{formatPrice(subtotal)}</span>
          </SummaryRow>

          <SummaryRow>
            <span>Frete</span>
            <span>{hasFreeShipping ? 'Gratis' : (shipping ? formatPrice(shipping.options[0].price) : 'Calcular')}</span>
          </SummaryRow>

          <SummaryRow $total style={{ paddingTop: '16px', borderTop: '1px solid #eee', marginTop: '16px' }}>
            <span>Total</span>
            <span>{formatPrice(total)}</span>
          </SummaryRow>

          <CheckoutButton to="/checkout">
            Finalizar Compra
          </CheckoutButton>

          <ContinueShoppingLink to="/">
            <FaArrowLeft /> Continuar comprando
          </ContinueShoppingLink>
        </CartSummary>
      </CartLayout>
    </PageContainer>
  );
};

export default Cart;
